import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { Plus, Trash2, CheckCircle, XCircle, DollarSign, TrendingUp, TrendingDown, Wallet, Printer, AlertCircle, Info, Camera, Eye, Users, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { printFinancialReport } from '@/utils/pdfExport';

const fmt = n => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;
const MONTHS_ID = ['', 'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
const EXPENSE_CATS = ['Kebersihan', 'Keamanan', 'Administrasi', 'Perlengkapan', 'Kegiatan', 'Darurat', 'Infrastruktur', 'Lainnya'];
const now = new Date();
const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);
const CITIZEN_TYPE_LABEL = { warga: 'Warga', pendatang: 'Pendatang', domisili: 'Domisili' };

const emptyExp = { category: 'Lainnya', description: '', amount: '', month: now.getMonth() + 1, year: now.getFullYear(), notes: '', photo_url: '' };

export default function FinancePage() {
    const { hasRole } = useAuth();
    const { t } = useLang();
    const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
    const [selYear, setSelYear] = useState(now.getFullYear());
    const [iuranList, setIuranList] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [unpaidList, setUnpaidList] = useState([]);
    const [report, setReport] = useState({});
    const [loading, setLoading] = useState(false);
    const [citizens, setCitizens] = useState([]);
    const [showIuranForm, setShowIuranForm] = useState(false);
    const [showExpForm, setShowExpForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [regional, setRegional] = useState(null);
    const [activeTab, setActiveTab] = useState('iuran');

    // Smart lock state
    const [smartRate, setSmartRate] = useState(null);
    const [smartLoading, setSmartLoading] = useState(false);
    const [iuranAmount, setIuranAmount] = useState('');
    const [iuranCitizenId, setIuranCitizenId] = useState('');
    const [iuranForm, setIuranForm] = useState({ month: now.getMonth() + 1, year: now.getFullYear(), notes: '' });
    const [expForm, setExpForm] = useState(emptyExp);
    const [expPhotoPreview, setExpPhotoPreview] = useState(null);
    const expPhotoRef = useRef();

    const canRecord = hasRole('bendahara', 'sie_umum', 'ketua_rt', 'wakil');
    const canApprove = hasRole('bendahara', 'ketua_rt');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [iu, ex, rep, unp] = await Promise.all([
                axios.get(`${API}/finance/iuran?month=${selMonth}&year=${selYear}&limit=100`, { withCredentials: true }),
                axios.get(`${API}/finance/expenses?month=${selMonth}&year=${selYear}`, { withCredentials: true }),
                axios.get(`${API}/finance/report?month=${selMonth}&year=${selYear}`, { withCredentials: true }),
                axios.get(`${API}/finance/iuran/unpaid?month=${selMonth}&year=${selYear}`, { withCredentials: true }),
            ]);
            setIuranList(iu.data.data || []);
            setExpenses(ex.data || []);
            setReport(rep.data || {});
            setUnpaidList(unp.data || []);
        } catch { toast.error('Gagal memuat data keuangan'); }
        setLoading(false);
    }, [selMonth, selYear]);

    useEffect(() => {
        load();
        axios.get(`${API}/citizens?limit=200`, { withCredentials: true }).then(r => setCitizens(r.data.data || [])).catch(() => {});
        axios.get(`${API}/settings/regional`, { withCredentials: true }).then(r => setRegional(r.data)).catch(() => {});
    }, [load]);

    // ── Smart Rate Calculation ──────────────────────────────────────────
    const fetchSmartRate = async (citizenId, month, year) => {
        if (!citizenId) return;
        setSmartLoading(true);
        try {
            const res = await axios.get(`${API}/finance/iuran/citizen-rate/${citizenId}?month=${month}&year=${year}`, { withCredentials: true });
            setSmartRate(res.data);
            setIuranAmount(String(res.data.minimum_payment));
        } catch { toast.error('Gagal mengambil tarif warga'); }
        setSmartLoading(false);
    };

    const handleCitizenSelect = (cid) => {
        setIuranCitizenId(cid);
        setSmartRate(null);
        fetchSmartRate(cid, iuranForm.month, iuranForm.year);
    };

    // Calculate months to be covered by current amount
    const getMonthsCovered = () => {
        if (!smartRate || !iuranAmount) return [];
        const base = smartRate.base_rate;
        const amt = Number(iuranAmount);
        if (base === 0 || amt < base) return [];
        const count = Math.floor(amt / base);
        const unpaid = smartRate.unpaid_months || [];
        const allMonths = [...unpaid];
        // Add future months if paying more than arrears
        let lastMonth = unpaid.length > 0 ? unpaid[unpaid.length - 1] : { month: iuranForm.month, year: iuranForm.year };
        while (allMonths.length < count) {
            let nm = lastMonth.month + 1, ny = lastMonth.year;
            if (nm > 12) { nm = 1; ny++; }
            allMonths.push({ month: nm, year: ny });
            lastMonth = { month: nm, year: ny };
        }
        return allMonths.slice(0, count);
    };

    const isValidAmount = () => {
        if (!smartRate) return true;
        const base = smartRate.base_rate;
        const amt = Number(iuranAmount);
        if (base === 0) return amt >= 0;
        return amt >= base && amt % base === 0;
    };

    const getAmountError = () => {
        if (!smartRate || !iuranAmount) return '';
        const base = smartRate.base_rate;
        const amt = Number(iuranAmount);
        if (base === 0) return '';
        if (amt < base) return `Minimal pembayaran: ${fmt(base)}`;
        if (amt % base !== 0) return `Nominal harus kelipatan ${fmt(base)} (misal: ${fmt(base)}, ${fmt(base * 2)}, ${fmt(base * 3)}...)`;
        return '';
    };

    // ── Iuran Save ─────────────────────────────────────────────────────
    const handleIuranSave = async () => {
        if (!iuranCitizenId) { toast.error('Pilih warga terlebih dahulu'); return; }
        const citizen = citizens.find(c => c.id === iuranCitizenId);
        if (!citizen) return;
        if (!isValidAmount()) { toast.error(getAmountError() || 'Nominal tidak valid'); return; }
        setSaving(true);
        try {
            const amt = Number(iuranAmount);
            const base = smartRate?.base_rate || amt;
            const monthsCovered = smartRate ? getMonthsCovered() : [{ month: iuranForm.month, year: iuranForm.year }];

            if (monthsCovered.length > 1 || (smartRate && getMonthsCovered().length > 1)) {
                await axios.post(`${API}/finance/iuran/multi`, {
                    citizen_id: citizen.id, kk_number: citizen.kk_number,
                    head_of_family: citizen.head_of_family,
                    start_month: monthsCovered[0]?.month || iuranForm.month,
                    start_year: monthsCovered[0]?.year || iuranForm.year,
                    amount_per_month: base,
                    months_to_cover: monthsCovered,
                    notes: iuranForm.notes
                }, { withCredentials: true });
                toast.success(`${monthsCovered.length} bulan iuran berhasil dicatat!`);
            } else {
                const target = monthsCovered[0] || { month: iuranForm.month, year: iuranForm.year };
                await axios.post(`${API}/finance/iuran`, {
                    citizen_id: citizen.id, kk_number: citizen.kk_number,
                    head_of_family: citizen.head_of_family,
                    month: target.month, year: target.year,
                    amount: amt, notes: iuranForm.notes
                }, { withCredentials: true });
                toast.success('Iuran berhasil dicatat!');
            }
            setShowIuranForm(false);
            setIuranCitizenId(''); setSmartRate(null); setIuranAmount('');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
        setSaving(false);
    };

    // Open form with pre-selected citizen (from "Bayar Sekarang")
    const openPayNow = (citizen) => {
        setShowIuranForm(true);
        setIuranCitizenId(citizen.id);
        setIuranForm(f => ({ ...f, month: selMonth, year: selYear }));
        fetchSmartRate(citizen.id, selMonth, selYear);
    };

    // ── Expense Photo ───────────────────────────────────────────────────
    const handleExpPhoto = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) { toast.error('Foto maksimal 3MB'); return; }
        const reader = new FileReader();
        reader.onload = (ev) => {
            setExpPhotoPreview(ev.target.result);
            setExpForm(f => ({ ...f, photo_url: ev.target.result }));
        };
        reader.readAsDataURL(file);
    };

    // ── Expense Save ────────────────────────────────────────────────────
    const handleExpSave = async () => {
        if (!expForm.description || !expForm.amount) { toast.error('Isi deskripsi dan nominal'); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/finance/expenses`, expForm, { withCredentials: true });
            toast.success('Pengeluaran dicatat');
            setShowExpForm(false); setExpForm(emptyExp); setExpPhotoPreview(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal menyimpan'); }
        setSaving(false);
    };

    const deleteIuran = async (id) => {
        if (!window.confirm('Hapus catatan iuran ini?')) return;
        try { await axios.delete(`${API}/finance/iuran/${id}`, { withCredentials: true }); toast.success('Dihapus'); load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    };
    const approveExp = async (id) => { try { await axios.put(`${API}/finance/expenses/${id}/approve`, {}, { withCredentials: true }); toast.success('Disetujui'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); } };
    const rejectExp = async (id) => { try { await axios.put(`${API}/finance/expenses/${id}/reject`, {}, { withCredentials: true }); toast.success('Ditolak'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); } };

    const monthsCovered = getMonthsCovered();
    const amtError = getAmountError();

        const handleExportExcel = async () => {
        try {
            toast.info("Menyiapkan file Excel...");
            const url = `${API}/finance/export-excel?year=${selYear}&month=${selMonth}`;
            const response = await axios.get(url, { responseType: 'blob', withCredentials: true });
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `Laporan_Kas_RT06_Menanggal_${selYear}_Bulan_${selMonth}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            toast.success("File Excel berhasil diunduh!");
        } catch (err) {
            console.error("Gagal export excel:", err);
            toast.error("Gagal mengunduh file Excel.");
        }
    };

    return (
        <div className="space-y-5" data-testid="finance-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h1 className="page-header text-2xl">{t.financeTitle}</h1>
                <div className="flex items-center gap-2 flex-wrap">
                    <Select value={String(selMonth)} onValueChange={v => setSelMonth(Number(v))}>
                        <SelectTrigger className="w-36 h-9" data-testid="month-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>{MONTHS_ID.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={String(selYear)} onValueChange={v => setSelYear(Number(v))}>
                        <SelectTrigger className="w-24 h-9" data-testid="year-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button variant="outline" className="h-9 gap-2 border-slate-300 text-slate-700 hover:bg-slate-50"
                        onClick={() => printFinancialReport(iuranList, expenses, report, regional, selYear, selMonth)} data-testid="print-finance-btn">
                        <Printer className="w-4 h-4" /> Cetak Print Out
                    </Button>
                    <Button variant="outline" className="h-9 gap-2 border-emerald-300 bg-emerald-50 text-[#065f46] hover:bg-emerald-100 font-semibold"
                        onClick={handleExportExcel} data-testid="export-excel-btn">
                        <FileSpreadsheet className="w-4 h-4 text-emerald-700" /> Export Excel
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { label: `Saldo Awal ${MONTHS_ID[selMonth]}`, value: fmt(report.opening_balance), icon: Wallet, color: "text-slate-700", bg: "bg-slate-100", note: "Bawaan bulan lalu" },
                    { label: `Pemasukan ${MONTHS_ID[selMonth]}`, value: fmt(report.iuran_total), icon: TrendingUp, color: "text-green-700", bg: "bg-green-50", note: `${report.paid_count || 0} KK lunas` },
                    { label: `Pengeluaran ${MONTHS_ID[selMonth]}`, value: fmt(report.expense_total), icon: TrendingDown, color: "text-orange-600", bg: "bg-orange-50", note: "Realisasi belanja" },
                    { label: "Sisa Saldo Kas Akhir", value: fmt(report.ending_balance ?? report.balance), icon: Wallet, color: (report.ending_balance ?? report.balance) >= 0 ? "text-[#065f46]" : "text-red-600", bg: "bg-emerald-50", note: "Kas nyata berjalan" },
                ].map(item => (
                    <div key={item.label} className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-slate-500 font-medium">{item.label}</p>
                            <div className={`w-8 h-8 ${item.bg} rounded-lg flex items-center justify-center`}>
                                <item.icon className={`w-4 h-4 ${item.color}`} />
                            </div>
                        </div>
                        <p className={`font-extrabold text-lg ${item.color}`} style={{ fontFamily: "Plus Jakarta Sans" }}>
                            {item.value}
                        </p>
                        {item.note && <p className="text-xs text-slate-400 mt-0.5">{item.note}</p>}
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <Tabs defaultValue="iuran" className="bg-white rounded-xl border border-slate-200 overflow-hidden" onValueChange={setActiveTab}>
                <div className="px-4 pt-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                    <TabsList className="bg-slate-100">
                        <TabsTrigger value="iuran" data-testid="tab-iuran">{t.contributions} ({iuranList.length})</TabsTrigger>
                        <TabsTrigger value="unpaid" data-testid="tab-unpaid">
                            <span className="flex items-center gap-1.5">
                                Belum Bayar
                                {unpaidList.length > 0 && <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 leading-none min-w-[18px] text-center">{unpaidList.length}</span>}
                            </span>
                        </TabsTrigger>
                        <TabsTrigger value="expenses" data-testid="tab-expenses">{t.expenses} ({expenses.length})</TabsTrigger>
                    </TabsList>
                    {canRecord && (
                        <div className="flex gap-2">
                            {activeTab !== 'expenses' ? (
                                <Button size="sm" onClick={() => { setShowIuranForm(true); setIuranCitizenId(''); setSmartRate(null); setIuranAmount(''); }}
                                    className="bg-primary hover:bg-primary/90 gap-1.5 h-8 text-xs" data-testid="add-iuran-btn">
                                    <Plus className="w-3.5 h-3.5" /> {t.recordIuran}
                                </Button>
                            ) : (
                                <Button size="sm" onClick={() => { setShowExpForm(true); setExpForm(emptyExp); setExpPhotoPreview(null); }}
                                    className="bg-secondary hover:bg-secondary/90 gap-1.5 h-8 text-xs text-white" data-testid="add-expense-btn-header">
                                    <Plus className="w-3.5 h-3.5" /> {t.addExpense}
                                </Button>
                            )}
                        </div>
                    )}
                </div>

                {/* Iuran Tab */}
                <TabsContent value="iuran" className="p-4">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50">
                                    <TableHead className="font-semibold text-slate-700">{t.name}</TableHead>
                                    <TableHead className="font-semibold text-slate-700 hidden md:table-cell">{t.kkNumber}</TableHead>
                                    <TableHead className="font-semibold text-slate-700">{t.amount}</TableHead>
                                    <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">{t.collectedBy}</TableHead>
                                    <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">{t.paymentDate}</TableHead>
                                    {canApprove && <TableHead />}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? [...Array(4)].map((_, i) => (
                                    <TableRow key={i}>{[...Array(5)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                                )) : iuranList.length === 0 ? (
                                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">{t.noData}</TableCell></TableRow>
                                ) : iuranList.map(i => (
                                    <TableRow key={i.id} className="hover:bg-slate-50">
                                        <TableCell className="font-medium text-slate-800">{i.head_of_family}</TableCell>
                                        <TableCell className="hidden md:table-cell font-mono text-sm text-slate-500">{i.kk_number}</TableCell>
                                        <TableCell className="font-semibold text-green-700">{fmt(i.amount)}</TableCell>
                                        <TableCell className="hidden lg:table-cell text-sm text-slate-500">{i.collector_name}</TableCell>
                                        <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                                            {i.payment_date ? new Date(i.payment_date).toLocaleDateString('id-ID') : '-'}
                                        </TableCell>
                                        {canApprove && (
                                            <TableCell>
                                                <button onClick={() => deleteIuran(i.id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </TabsContent>

                {/* Belum Bayar Tab */}
                <TabsContent value="unpaid" className="p-4" data-testid="unpaid-tab">
                    {unpaidList.length === 0 ? (
                        <div className="py-16 text-center">
                            <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                            <p className="text-slate-500 font-medium">Semua warga sudah bayar!</p>
                            <p className="text-slate-400 text-sm mt-1">{MONTHS_ID[selMonth]} {selYear}</p>
                        </div>
                    ) : (
                        <div>
                            <p className="text-sm text-slate-500 mb-3">
                                <span className="font-bold text-red-600">{unpaidList.length} warga</span> belum membayar iuran {MONTHS_ID[selMonth]} {selYear}
                            </p>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-slate-50">
                                            <TableHead className="font-semibold text-slate-700">{t.name}</TableHead>
                                            <TableHead className="font-semibold text-slate-700 hidden md:table-cell">Tipe</TableHead>
                                            <TableHead className="font-semibold text-slate-700 hidden md:table-cell">{t.phone}</TableHead>
                                            {canRecord && <TableHead className="font-semibold text-slate-700 text-right">Aksi</TableHead>}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {unpaidList.map(c => (
                                            <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`unpaid-row-${c.id}`}>
                                                <TableCell>
                                                    <p className="font-semibold text-slate-800 text-sm">{c.head_of_family}</p>
                                                    <p className="text-xs text-slate-400 font-mono">{c.kk_number}</p>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell">
                                                    <div className="flex flex-wrap gap-1">
                                                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                                            {CITIZEN_TYPE_LABEL[c.citizen_type] || 'Warga'}
                                                        </span>
                                                        {c.has_business && <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">Usaha</span>}
                                                        {c.is_underprivileged && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600">KM</span>}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="hidden md:table-cell text-sm text-slate-500">{c.phone || '—'}</TableCell>
                                                {canRecord && (
                                                    <TableCell className="text-right">
                                                        <Button size="sm" onClick={() => openPayNow(c)} data-testid={`pay-now-${c.id}`}
                                                            className="bg-green-600 hover:bg-green-700 gap-1.5 h-8 text-xs">
                                                            <DollarSign className="w-3 h-3" /> Bayar Sekarang
                                                        </Button>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    )}
                </TabsContent>

                {/* Expenses Tab */}
                <TabsContent value="expenses" className="p-4 space-y-4">
                    <div className="flex justify-between items-center">
                        <p className="text-sm text-slate-500">{expenses.length} pengeluaran</p>
                    </div>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50">
                                    <TableHead className="font-semibold text-slate-700">{t.expenseCategory}</TableHead>
                                    <TableHead className="font-semibold text-slate-700">{t.expenseDesc}</TableHead>
                                    <TableHead className="font-semibold text-slate-700">{t.amount}</TableHead>
                                    <TableHead className="font-semibold text-slate-700">Nota</TableHead>
                                    <TableHead className="font-semibold text-slate-700">{t.status}</TableHead>
                                    {canApprove && <TableHead className="font-semibold text-slate-700 text-right">{t.actions}</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? [...Array(3)].map((_, i) => (
                                    <TableRow key={i}>{[...Array(5)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                                )) : expenses.length === 0 ? (
                                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">{t.noData}</TableCell></TableRow>
                                ) : expenses.map(e => (
                                    <TableRow key={e.id} className="hover:bg-slate-50">
                                        <TableCell><span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">{e.category}</span></TableCell>
                                        <TableCell className="text-slate-700">{e.description}</TableCell>
                                        <TableCell className="font-semibold text-slate-800">{fmt(e.amount)}</TableCell>
                                        <TableCell>
                                            {e.photo_url ? (
                                                <button onClick={() => window.open(e.photo_url, '_blank')} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`view-receipt-${e.id}`}>
                                                    <Eye className="w-3.5 h-3.5" /> Lihat Nota
                                                </button>
                                            ) : <span className="text-xs text-slate-300">—</span>}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${e.status === 'approved' ? 'badge-approved' : e.status === 'rejected' ? 'badge-rejected' : 'badge-pending'}`}>
                                                {e.status === 'approved' ? t.approved : e.status === 'rejected' ? t.rejected : t.pending}
                                            </span>
                                        </TableCell>
                                        {canApprove && (
                                            <TableCell className="text-right">
                                                {e.status === 'pending' && (
                                                    <div className="flex justify-end gap-1">
                                                        <button onClick={() => approveExp(e.id)} data-testid={`approve-exp-${e.id}`}
                                                            className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors">
                                                            <CheckCircle className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => rejectExp(e.id)} data-testid={`reject-exp-${e.id}`}
                                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors">
                                                            <XCircle className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Smart Iuran Form Dialog */}
            <Dialog open={showIuranForm} onOpenChange={v => { if (!v) { setShowIuranForm(false); setIuranCitizenId(''); setSmartRate(null); setIuranAmount(''); } }}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>{t.recordIuran}</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>Pilih Warga *</Label>
                            <Select value={iuranCitizenId} onValueChange={handleCitizenSelect} data-testid="iuran-citizen-select">
                                <SelectTrigger><SelectValue placeholder="Pilih warga..." /></SelectTrigger>
                                <SelectContent>
                                    {citizens.map(c => <SelectItem key={c.id} value={c.id}>{c.head_of_family} — {c.kk_number}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Smart Rate Info */}
                        {smartLoading && <div className="flex items-center gap-2 text-sm text-slate-500"><div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Menghitung tarif...</div>}
                        {smartRate && !smartLoading && (
                            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
                                <div className="flex items-start gap-2">
                                    <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-sm">
                                        <p className="font-semibold text-blue-800">Tarif Iuran Warga</p>
                                        <p className="text-blue-700">Tarif dasar: <span className="font-bold">{fmt(smartRate.base_rate)}/bulan</span></p>
                                        {smartRate.arrears_count > 0 && (
                                            <p className="text-amber-700 font-semibold mt-1">
                                                ⚠ Tunggakan: {smartRate.arrears_count} bulan — min. bayar {fmt(smartRate.minimum_payment)}
                                            </p>
                                        )}
                                        {smartRate.arrears_count === 0 && <p className="text-green-700">Tidak ada tunggakan</p>}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>{t.month}</Label>
                                <Select value={String(iuranForm.month)} onValueChange={v => {
                                    const nm = Number(v);
                                    setIuranForm(f => ({ ...f, month: nm }));
                                    if (iuranCitizenId) fetchSmartRate(iuranCitizenId, nm, iuranForm.year);
                                }}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{MONTHS_ID.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t.year}</Label>
                                <Input type="number" value={iuranForm.year} onChange={e => setIuranForm(f => ({ ...f, year: Number(e.target.value) }))} />
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label>{t.amount} *</Label>
                            <Input type="number" value={iuranAmount}
                                onChange={e => setIuranAmount(e.target.value)}
                                step={smartRate?.base_rate || 1000}
                                min={smartRate?.minimum_payment || 0}
                                placeholder={smartRate ? `Min: ${fmt(smartRate.minimum_payment)}` : '50000'}
                                className={amtError ? 'border-red-400 focus:ring-red-400' : ''}
                                data-testid="iuran-amount-input" />
                            {amtError && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{amtError}</p>}
                        </div>

                        {/* Months coverage preview */}
                        {smartRate && monthsCovered.length > 0 && isValidAmount() && (
                            <div className="rounded-xl bg-green-50 border border-green-200 p-3">
                                <p className="text-xs font-semibold text-green-800 mb-2">
                                    Pembayaran mencakup {monthsCovered.length} bulan:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                    {monthsCovered.map((slot, i) => (
                                        <span key={i} className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-medium">
                                            {MONTHS_ID[slot.month]} {slot.year}
                                        </span>
                                    ))}
                                </div>
                                <p className="text-xs text-green-700 mt-2">Total: {fmt(Number(iuranAmount))}</p>
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <Label>Catatan</Label>
                            <Input value={iuranForm.notes} onChange={e => setIuranForm(f => ({ ...f, notes: e.target.value }))} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowIuranForm(false)}>{t.cancel}</Button>
                        <Button onClick={handleIuranSave} disabled={saving || !isValidAmount() || !iuranCitizenId}
                            className="bg-primary hover:bg-primary/90" data-testid="save-iuran-btn">
                            {saving ? 'Menyimpan...' : t.save}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Expense Form Dialog */}
            <Dialog open={showExpForm} onOpenChange={setShowExpForm}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{t.addExpense}</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label>{t.expenseCategory}</Label>
                            <Select value={expForm.category} onValueChange={v => setExpForm(f => ({ ...f, category: v }))}>
                                <SelectTrigger data-testid="exp-category-select"><SelectValue /></SelectTrigger>
                                <SelectContent>{EXPENSE_CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.expenseDesc} *</Label>
                            <Input value={expForm.description} onChange={e => setExpForm(f => ({ ...f, description: e.target.value }))} data-testid="exp-desc-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t.amount} *</Label>
                            <Input type="number" value={expForm.amount} onChange={e => setExpForm(f => ({ ...f, amount: e.target.value }))} data-testid="exp-amount-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>{t.month}</Label>
                                <Select value={String(expForm.month)} onValueChange={v => setExpForm(f => ({ ...f, month: Number(v) }))}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>{MONTHS_ID.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>{t.year}</Label>
                                <Input type="number" value={expForm.year} onChange={e => setExpForm(f => ({ ...f, year: Number(e.target.value) }))} />
                            </div>
                        </div>
                        {/* Photo Upload */}
                        <div className="space-y-1.5">
                            <Label className="flex items-center gap-2"><Camera className="w-4 h-4 text-slate-500" /> Foto Nota / Kwitansi <span className="text-slate-400 font-normal">(opsional)</span></Label>
                            <input type="file" accept="image/*" ref={expPhotoRef} onChange={handleExpPhoto} className="hidden" data-testid="exp-photo-input" />
                            {expPhotoPreview ? (
                                <div className="relative">
                                    <img src={expPhotoPreview} alt="Nota" className="w-full max-h-40 object-contain rounded-xl border border-slate-200 bg-slate-50" />
                                    <button onClick={() => { setExpPhotoPreview(null); setExpForm(f => ({ ...f, photo_url: '' })); }}
                                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow text-red-500 hover:bg-red-50 transition-colors">
                                        <XCircle className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <button onClick={() => expPhotoRef.current?.click()} data-testid="upload-receipt-btn"
                                    className="w-full border-2 border-dashed border-slate-300 rounded-xl py-6 flex flex-col items-center gap-2 text-slate-400 hover:border-primary hover:text-primary transition-colors">
                                    <Camera className="w-8 h-8" />
                                    <span className="text-sm">Klik untuk unggah foto nota</span>
                                    <span className="text-xs">JPG/PNG, maks. 3MB</span>
                                </button>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Catatan</Label>
                            <Input value={expForm.notes} onChange={e => setExpForm(f => ({ ...f, notes: e.target.value }))} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowExpForm(false)}>{t.cancel}</Button>
                        <Button onClick={handleExpSave} disabled={saving} className="bg-secondary hover:bg-secondary/90 text-white" data-testid="save-expense-btn">
                            {saving ? 'Menyimpan...' : t.save}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
